import { Helmet } from 'react-helmet-async';

const SEO = ({
  title = 'BusinessMint PR',
  description = 'Get featured in top-tier publications. BusinessMint PR connects brands with premium media outlets for guaranteed press coverage and enhanced online visibility.',
  keywords = 'PR services, media coverage, press release, brand visibility, publication placement, digital PR, media outreach, BusinessMint',
  canonical,
  ogType = 'website',
  ogImage = '/og-image.png',
  twitterCard = 'summary_large_image',
  noindex = false,
  structuredData = null,
}) => {
  const siteUrl = process.env.REACT_APP_BACKEND_URL || 'https://businessmint.in';
  const fullTitle = title === 'BusinessMint PR' ? title : `${title} | BusinessMint PR`;
  const canonicalUrl = canonical ? `${siteUrl}${canonical}` : undefined;
  const imageUrl = ogImage || '/og-image.png';
  const ogImageUrl = imageUrl.startsWith('http') ? imageUrl : `${siteUrl}${imageUrl}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Open Graph Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:site_name" content="BusinessMint PR" />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

// Pre-configured SEO for common pages
export const HomeSEO = () => (
  <SEO
    title="BusinessMint PR"
    description="Get featured in top-tier publications with BusinessMint PR. We connect brands with premium media outlets for guaranteed press coverage, enhanced credibility, and improved online visibility."
    keywords="PR services, media coverage, press release distribution, brand visibility, publication placement, digital PR, media outreach, guaranteed coverage, BusinessMint PR"
    canonical="/"
    structuredData={{
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "BusinessMint PR",
      "description": "Premium PR and media coverage services for brands and businesses",
      "url": process.env.REACT_APP_BACKEND_URL || "https://businessmint.in",
      "logo": `${process.env.REACT_APP_BACKEND_URL || "https://businessmint.in"}/logo.png`,
      "contactPoint": {
        "@type": "ContactPoint",
        "email": "support@businessmint.in",
        "contactType": "customer service"
      },
      "sameAs": []
    }}
  />
);

export const MarketplaceSEO = () => (
  <SEO
    title="Browse Publications"
    description="Explore our curated marketplace of premium publications. Find the perfect media outlet for your brand from our selection of top-tier news sites, magazines, and industry publications."
    keywords="media marketplace, publication listings, PR packages, press release placement, media buying, sponsored content, guest posts, brand mentions"
    canonical="/marketplace"
    structuredData={{
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "BusinessMint PR Publications",
      "description": "Curated list of premium publications for PR and media coverage",
      "itemListOrder": "https://schema.org/ItemListUnordered"
    }}
  />
);

export const PublicationDetailSEO = ({ publication }) => (
  <SEO
    title={publication?.name || 'Publication Details'}
    description={publication?.description?.substring(0, 160) || `Get featured on ${publication?.name}. Professional PR placement with guaranteed coverage and fast turnaround.`}
    keywords={`${publication?.name}, ${publication?.category}, PR placement, media coverage, sponsored content, guest post`}
    canonical={`/publication/${publication?.id}`}
    ogType="product"
    structuredData={publication ? {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": `PR Coverage on ${publication.name}`,
      "description": publication.description,
      "category": publication.category,
      "brand": {
        "@type": "Brand",
        "name": "BusinessMint PR"
      },
      "offers": {
        "@type": "AggregateOffer",
        "priceCurrency": "INR",
        "availability": "https://schema.org/InStock"
      }
    } : null}
  />
);

export const CheckoutSEO = () => (
  <SEO
    title="Checkout"
    description="Complete your PR package purchase securely. Get featured in top publications with BusinessMint PR's guaranteed media coverage services."
    keywords="checkout, buy PR package, media coverage purchase, secure payment"
    canonical="/checkout"
    noindex={true}
  />
);

export const MyOrdersSEO = () => (
  <SEO
    title="My Orders"
    description="Track and manage your PR orders. View order status, submit content, and monitor your media coverage progress."
    canonical="/my-orders"
    noindex={true}
  />
);

export const TrackOrderSEO = ({ orderId }) => (
  <SEO
    title="Track Order"
    description="Track your PR order status in real-time. View content submission status, publication timeline, and live article links."
    noindex={true}
  />
);

export const TermsSEO = () => (
  <SEO
    title="Terms of Service"
    description="Read BusinessMint PR's terms of service. Understand our policies, user agreements, and service conditions for our PR and media coverage services."
    keywords="terms of service, user agreement, PR service terms, BusinessMint terms"
    canonical="/terms"
  />
);

export const PrivacySEO = () => (
  <SEO
    title="Privacy Policy"
    description="BusinessMint PR's privacy policy. Learn how we collect, use, and protect your personal information when you use our PR services."
    keywords="privacy policy, data protection, personal information, BusinessMint privacy"
    canonical="/privacy"
  />
);

export const RefundSEO = () => (
  <SEO
    title="Refund Policy"
    description="BusinessMint PR's refund policy. Understand our refund conditions, cancellation policy, and money-back guarantee terms."
    keywords="refund policy, cancellation policy, money back guarantee, BusinessMint refund"
    canonical="/refund"
  />
);

export const LoginSEO = () => (
  <SEO
    title="Login"
    description="Sign in to your BusinessMint PR account to manage orders, track publications, and access your dashboard."
    canonical="/login"
    noindex={true}
  />
);

export const RegisterSEO = () => (
  <SEO
    title="Create Account"
    description="Create your BusinessMint PR account to start getting featured in top publications. Sign up for premium PR services."
    canonical="/register"
    noindex={true}
  />
);

export default SEO;
